/**
 * Stage E — Electrical Engineering panel.
 *
 * Sub-stage routing follows the project's authoritative
 * `stages_catalog.py` numbering (STAGES_PROCESS):
 *   E.1 · Load & Power Studies                    → E1LoadPower
 *   E.2 · Hazardous Area Classification           → E2HazArea
 *   E.3 · Diagrams (SLD & Schematics)             → E3SldDiagrams
 *   E.4 · Earthing, Lightning & Protection        → E4Earthing
 *   E.5 · Cable Sizing & Schedule                 → E5Cable
 *   E.6 · Electrical MTO                          → E6MTO
 *
 *  ⚠ Ordering NOTE: the PROMPT_STAGE_E_FIXES.md spec labels the same
 *   engineering topic differently (spec E.1 = HazArea, spec E.2 = Load).
 *   All engines are decoupled from the numbering — this panel routes
 *   the CATALOG code to the correct engineering-topic component.
 */
import React, { useEffect, useState } from "react";
import api from "../lib/api";

const _fmt = (n, d = 1) => (n == null ? "-"
    : typeof n === "number" ? n.toFixed(d) : n);

function StatTile({ label, value }) {
  return (
    <div className="rounded-md border border-neutral-800 bg-neutral-900/60 p-3">
      <div className="text-2xl font-bold text-neutral-100">{value}</div>
      <div className="mt-1 text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
    </div>
  );
}

function Section({ title, children, right }) {
  return (
    <div className="rounded-md border border-neutral-800 bg-neutral-950 p-4">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-semibold text-neutral-200">{title}</div>
        {right}
      </div>
      {children}
    </div>
  );
}

function Table({ cols, rows }) {
  return (
    <div className="overflow-x-auto rounded border border-neutral-800">
      <table className="w-full text-xs">
        <thead className="bg-neutral-900 text-neutral-400">
          <tr>{cols.map(([, l]) => <th key={l} className="px-2 py-2 text-left font-medium">{l}</th>)}</tr>
        </thead>
        <tbody>
          {(rows || []).map((r, i) => (
            <tr key={i} className="border-t border-neutral-800 text-neutral-200">
              {cols.map(([k]) => <td key={k} className="px-2 py-1.5">{_fmt(r?.[k])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ═════════════════════ E.1 · Load & Power Studies ══════════════ */
function E1LoadPower({ projectId, res }) {
  const ls = res?.load_summary || {};
  const xfmr = res?.transformer || {};
  const pf = res?.pf_correction || {};
  const emerg = res?.emergency_power || {};
  const ms = res?.motor_starting || {};
  const sc = res?.short_circuit || {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Total loads"    value={ls.count ?? 0} />
        <StatTile label="Motors"          value={ls.motor_count ?? 0} />
        <StatTile label="Demand kW"       value={_fmt(ls.total_demand_kW, 0)} />
        <StatTile label="Transformer kVA" value={xfmr.rated_kVA ?? "-"} />
      </div>
      <Section title="Voltage levels">
        <div className="grid grid-cols-3 gap-3 text-sm text-neutral-300">
          <div>LV: <b>{ls.voltage_levels?.LV ?? "-"} V</b></div>
          <div>MV: <b>{ls.voltage_levels?.MV ?? "-"} V</b></div>
          <div>HV: <b>{ls.voltage_levels?.HV ?? "-"} V</b></div>
        </div>
        <div className="mt-1 text-xs text-neutral-500">
          Country standard: {ls.voltage_levels?.std ?? "-"} · {ls.voltage_levels?.phase ?? ""}
        </div>
      </Section>
      <Section title="Load list (first 20)">
        <Table cols={[
          ["tag","Tag"], ["description","Description"], ["type","Type"],
          ["rated_kW","kW"], ["voltage_V","V"], ["power_factor","PF"],
          ["FLC_A","FLC"], ["starting_method","Start"], ["ex_class","Ex"],
        ]} rows={(ls.loads || []).slice(0, 20)} />
      </Section>
      <Section title="Motor starting voltage dip (first 6)">
        <Table cols={[["tag","Tag"], ["rated_kW","kW"], ["starting_method","Method"],
                        ["FLC_A","FLC"], ["starting_current_A","I_start"],
                        ["recommendation","Result"]]}
                 rows={(ms.results || []).slice(0, 6)} />
      </Section>
      <div className="grid gap-4 md:grid-cols-2">
        <Section title="Power factor correction">
          <div className="text-xs text-neutral-300 space-y-1">
            <div>Existing PF : <b>{pf.existing_pf ?? "-"}</b></div>
            <div>Target PF   : <b>{pf.target_pf ?? "-"}</b></div>
            <div>Q required  : <b>{_fmt(pf.Q_required_kVAr, 0)} kVAr</b></div>
            <div>Bank config : <b>{pf.number_of_steps} × {pf.capacitor_bank_step_kVAr} kVAr</b></div>
          </div>
        </Section>
        <Section title="Short-circuit at LV MCC">
          <div className="text-xs text-neutral-300 space-y-1">
            <div>Symmetrical  : <b>{sc.lv_mcc_bus?.sym_kA} kA</b></div>
            <div>Asymmetrical : <b>{sc.lv_mcc_bus?.asym_kA} kA</b></div>
            <div>Switchgear   : <b>{sc.lv_mcc_bus?.switchgear_rating_kA} kA rating</b></div>
            <div>Check        : <b>{sc.lv_mcc_bus?.check}</b></div>
          </div>
        </Section>
      </div>
      <Section title="Emergency power (DG + UPS)">
        <div className="grid grid-cols-2 gap-4 text-xs text-neutral-300">
          <div>DG capacity: <b>{emerg.dg?.capacity_kVA} kVA</b> · {emerg.dg?.redundancy}</div>
          <div>UPS capacity: <b>{emerg.ups?.capacity_kVA} kVA</b> · {emerg.ups?.autonomy_min} min</div>
          <div>Essential loads: <b>{emerg.essential_load_count}</b> ({_fmt(emerg.essential_load_kW,0)} kW)</div>
          <div>Critical loads: <b>{emerg.critical_load_count}</b> ({_fmt(emerg.critical_load_kW,0)} kW)</div>
        </div>
      </Section>
      <div className="flex gap-2">
        <button className="btn btn-secondary text-xs"
                  onClick={() => window.open(
                    `${api.defaults.baseURL}/projects/${projectId}/stage-e/load-list/svg?_t=${Date.now()}`, "_blank")}
                  data-testid="stage-e-load-list-svg-btn">
          Preview Load List SVG
        </button>
        <button className="btn btn-primary text-xs"
                  onClick={async () => {
                    const r = await api.post(`/projects/${projectId}/stage-e/load-list/dxf`,
                                                    {equipment: [], design_basis: {}, country: "IN"},
                                                    { responseType: "blob" });
                    const url = URL.createObjectURL(r.data);
                    const a = document.createElement("a");
                    a.href = url; a.download = "load-list.dxf"; a.click();
                  }}
                  data-testid="stage-e-load-list-dxf-btn">
          Download Load List DXF
        </button>
      </div>
    </div>
  );
}

/* ═════════════════════ E.2 · Hazardous Area ═══════════════════ */
function E2HazArea({ projectId }) {
  const [components, setComponents] = useState(null);
  const [standard, setStandard] = useState(null);
  useEffect(() => {
    api.get(`/projects/${projectId}/stage-e/hazarea/components`)
      .then((r) => setComponents(r.data)).catch(() => {});
    api.get(`/projects/${projectId}/stage-e/hazarea/standards?country=IN`)
      .then((r) => setStandard(r.data)).catch(() => {});
  }, [projectId]);
  return (
    <div className="space-y-4">
      <Section title="Country standard (haz-area)">
        {!standard && <div className="text-xs text-neutral-500">Loading standards…</div>}
        {standard && (
          <div className="text-xs text-neutral-300 grid grid-cols-2 gap-2">
            <div>Code       : <b>{standard.code}</b></div>
            <div>Certification: <b>{standard.certification}</b></div>
            {standard.installation && <div>Installation: <b>{standard.installation}</b></div>}
            {standard.dual_marking && <div>Dual marking (Zone + Div): <b>YES</b></div>}
          </div>
        )}
      </Section>
      <Section title={`Component library (${components?.count ?? 0} gases)`}>
        <div className="overflow-x-auto max-h-64 rounded border border-neutral-800">
          <table className="w-full text-xs">
            <thead className="bg-neutral-900 text-neutral-400">
              <tr>{["Component","LEL %","AIT °C","MW","Group","T-class"]
                .map((h) => <th key={h} className="px-2 py-1.5 text-left">{h}</th>)}</tr>
            </thead>
            <tbody>
              {(components?.components || []).slice(0, 30).map((c) => (
                <tr key={c.name} className="border-t border-neutral-800 text-neutral-200">
                  <td className="px-2 py-1">{c.name}</td>
                  <td className="px-2 py-1">{c.LEL_pct}</td>
                  <td className="px-2 py-1">{c.AIT_C}</td>
                  <td className="px-2 py-1">{c.molar_mass_gmol}</td>
                  <td className="px-2 py-1">{c.gas_group}</td>
                  <td className="px-2 py-1">{c.temp_class}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
      <div className="flex gap-2">
        <button className="btn btn-secondary text-xs"
                  onClick={async () => {
                    const r = await api.post(`/projects/${projectId}/stage-e/hazarea/svg`,
                                                    {equipment_positions: [
                                                       {tag: "P-101", x_m: 20, y_m: 20, length_m: 2, width_m: 1.5},
                                                       {tag: "T-101", x_m: 60, y_m: 30, length_m: 8, width_m: 8},
                                                       {tag: "F-1",   x_m: 90, y_m: 55, length_m: 3, width_m: 3}],
                                                     zone_records: [
                                                       {tag: "P-101", zone: "2", extent_m: 3, gas_group: "IIA", temp_class: "T2", protection: "Ex n"},
                                                       {tag: "T-101", zone: "1", extent_m: 5, gas_group: "IIA", temp_class: "T3", protection: "Ex d"},
                                                       {tag: "F-1",   zone: "0", extent_m: 2, gas_group: "IIA", temp_class: "T3", protection: "Ex ia"}],
                                                     plot_size_m: [120, 80], country: "IN"});
                    const blob = new Blob([r.data], {type: "image/svg+xml"});
                    window.open(URL.createObjectURL(blob), "_blank");
                  }}
                  data-testid="stage-e-hazarea-svg-btn">
          Preview HazArea Drawing SVG
        </button>
      </div>
      <div className="rounded-md border border-neutral-800 bg-neutral-950 p-4 text-xs text-neutral-400">
        Ex-protection selector: <code>POST /stage-e/hazarea/ex-protection</code> ·
        Zone-extent calc: <code>POST /stage-e/hazarea/zone-extent</code> ·
        NEC dual-marking: <code>POST /stage-e/hazarea/nec-conversion</code>
      </div>
    </div>
  );
}

/* ═════════════════════ E.3 · SLD & Schematics ═════════════════ */
function E3SldDiagrams({ projectId, res }) {
  const arc = res?.arc_flash?.results || {};
  const prot = res?.protection_coordination || {};
  return (
    <div className="space-y-4">
      <Section title="Arc-flash (IEEE 1584-2018)">
        <div className="grid grid-cols-2 gap-3 text-xs">
          {Object.entries(arc).map(([bus, v]) => (
            <div key={bus} className="rounded border border-neutral-800 p-2">
              <div className="font-semibold text-neutral-200">{bus.toUpperCase()}</div>
              <div>V   : {v.voltage_kV} kV</div>
              <div>Ibf : {v.bolted_fault_kA} kA</div>
              <div>IE  : <b>{v.incident_energy_cal_cm2} cal/cm²</b></div>
              <div>Bnd : {v.arc_flash_boundary_mm} mm</div>
              <div className="mt-1 text-[11px] text-yellow-400">PPE: {v.ppe_category}</div>
            </div>
          ))}
        </div>
      </Section>
      <Section title="Protection coordination">
        <div className="text-xs text-neutral-300">
          Curves: <b>{(prot.curves || []).length}</b> ·
          Discrimination: <b>{prot.discrimination_ok ? "OK" : "REVIEW"}</b>
        </div>
        {(prot.issues || []).length > 0 && (
          <ul className="mt-1 list-disc pl-5 text-[11px] text-amber-300">
            {prot.issues.slice(0, 4).map((i, k) => <li key={k}>{i}</li>)}
          </ul>
        )}
      </Section>
      <div className="flex gap-2">
        <button className="btn btn-secondary text-xs"
                  onClick={async () => {
                    const r = await api.post(`/projects/${projectId}/stage-e/sld/render-svg`,
                      {sld_data: {mccs: [{name: "MCC-1", feeders: [{tag: "M-101", kW: 30}, {tag: "M-102", kW: 45}]},
                                                {name: "MCC-2", feeders: [{tag: "K-201", kW: 550}]}],
                                     essential: {dg: {capacity_kVA: 750}, ups: {capacity_kVA: 30}}},
                       country: "IN", sheet_no: 1});
                    const blob = new Blob([r.data], {type: "image/svg+xml"});
                    window.open(URL.createObjectURL(blob), "_blank");
                  }}
                  data-testid="stage-e-sld-svg-btn">
          Preview SLD SVG
        </button>
        <button className="btn btn-primary text-xs"
                  onClick={async () => {
                    const r = await api.post(`/projects/${projectId}/stage-e/sld/render-dxf`,
                      {sld_data: {mccs: [{name: "MCC-1", feeders: [{tag: "M-101"}]}], essential: {}},
                       country: "IN"},
                      {responseType: "blob"});
                    const url = URL.createObjectURL(r.data);
                    const a = document.createElement("a");
                    a.href = url; a.download = "sld.dxf"; a.click();
                  }}
                  data-testid="stage-e-sld-dxf-btn">
          Download SLD DXF
        </button>
      </div>
      <div className="rounded-md border border-neutral-800 bg-neutral-950 p-4 text-xs text-neutral-400">
        SLD auto-gen (SVG+DXF), IEC 60617 symbols, multi-sheet organisation,
        protection coordination, IEEE 1584 arc-flash, CT/PT sizing —
        all available at <code>/stage-e/sld/*</code> and <code>/stage-e/protection-coordination</code>,
        <code>/stage-e/arc-flash</code>, <code>/stage-e/ct-pt-sizing</code>.
      </div>
    </div>
  );
}

/* ═════════════════════ E.4 · Earthing & Lightning ═════════════ */
function E4Earthing({ projectId, res }) {
  const grid = res?.earthing_grid || {};
  const lps = res?.lightning_protection || {};
  const sched = res?.earthing_schedule_first10 || [];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Grid R (Ω)" value={grid.grid_resistance_ohm ?? "-"} />
        <StatTile label="Rods" value={grid.rods_count ?? "-"} />
        <StatTile label="Conductor mm²" value={grid.conductor_mm2 ?? "-"} />
        <StatTile label="LPS Level" value={lps.protection_level ?? "-"} />
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <Section title="Earthing grid (IEEE 80)">
          <div className="text-xs text-neutral-300 space-y-1">
            <div>Mesh spacing: <b>{grid.mesh_spacing_m} m</b></div>
            <div>Touch V tolerable/actual: <b>{grid.touch_voltage?.tolerable_V} / {grid.touch_voltage?.actual_V} V</b>  ({grid.touch_voltage?.check})</div>
            <div>Step V tolerable/actual : <b>{grid.step_voltage?.tolerable_V} / {grid.step_voltage?.actual_V} V</b> ({grid.step_voltage?.check})</div>
            <div>Reference: {grid.reference}</div>
          </div>
        </Section>
        <Section title="Lightning protection (IEC 62305)">
          <div className="text-xs text-neutral-300 space-y-1">
            <div>Risk ratio    : <b>{lps.risk_ratio}</b></div>
            <div>Rolling sphere: <b>{lps.rolling_sphere_radius_m} m</b></div>
            <div>Down conductors: <b>{lps.down_conductors?.count}</b>, spacing {lps.down_conductors?.spacing_m} m</div>
            <div>SPD: {lps.spd}</div>
          </div>
        </Section>
      </div>
      <Section title="Earthing schedule (first 10)">
        <Table cols={[
          ["tag","Tag"], ["description","Description"], ["phase_mm2","Phase mm²"],
          ["earth_conductor_mm2","Earth mm²"], ["connection_method","Method"],
          ["earth_pits","Pits"], ["remarks","Remarks"]
        ]} rows={sched} />
      </Section>
      <button className="btn btn-secondary text-xs w-fit"
                onClick={async () => {
                  const r = await api.post(`/projects/${projectId}/stage-e/earthing/layout-svg`,
                                                  {plot_size_m: [100, 60], equipment_positions: [], grid: {}, lps: {}});
                  const blob = new Blob([r.data], {type: "image/svg+xml"});
                  window.open(URL.createObjectURL(blob), "_blank");
                }}
                data-testid="stage-e-earthing-svg-btn">
        Preview Earthing Layout SVG
      </button>
    </div>
  );
}

/* ═════════════════════ E.5 · Cable Engineering ════════════════ */
function E5Cable({ res }) {
  const samples = res?.cable_sizing_sample || [];
  const sched = res?.cable_schedule_first10 || [];
  const trays = res?.cable_trays || [];
  const drums = res?.cable_drums || [];
  return (
    <div className="space-y-4">
      <Section title="Sample rigorous cable sizing (IEC 60364-5-52)">
        <Table cols={[
          ["tag","Tag"], ["load_kW","kW"], ["voltage_V","V"],
          ["design_current_A","Ib A"], ["required_Iz_A","Iz req A"],
          ["cable_size_mm2","Size mm²"], ["vdrop_running_pct","ΔV %"],
        ]} rows={samples} />
      </Section>
      <Section title="Cable schedule (first 10)">
        <Table cols={[
          ["cable_no","Cable No"], ["from","From"], ["to","To"],
          ["size_mm2","Size mm²"], ["cores","Cores"],
          ["length_m","L m"], ["tray_conduit","Tray"], ["gland_size","Gland"]
        ]} rows={sched} />
      </Section>
      <div className="grid gap-4 md:grid-cols-2">
        <Section title="Cable trays">
          <Table cols={[["tray_id","Tray"], ["cable_count","N"],
                          ["tray_width_mm","Width mm"], ["fill_limit_pct","Fill %"]]}
                    rows={trays} />
        </Section>
        <Section title="Drumming schedule">
          <Table cols={[["cable_type","Type"], ["size_mm2","mm²"],
                          ["total_length_m","Total m"], ["drum_count","Drums"]]}
                    rows={drums} />
        </Section>
      </div>
    </div>
  );
}

/* ═════════════════════ E.6 · Electrical MTO ════════════════════ */
function E6MTO({ projectId, res }) {
  const mto = res?.electrical_mto?.categories || {};
  const comp = res?.completeness || {};
  const light = res?.lighting || {};
  const cables = mto?.cables_by_type_size || [];
  const trays = mto?.cable_trays_m || {};
  const motors = mto?.motors_by_size || {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Transformers" value={(mto.transformers || []).length} />
        <StatTile label="Total motors" value={res?.electrical_mto?.count_summary?.total_motors ?? 0} />
        <StatTile label="Total cable m" value={_fmt(res?.electrical_mto?.count_summary?.total_cable_m, 0)} />
        <StatTile label="Completeness %" value={_fmt(comp.percent_complete, 0)} />
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <Section title="Motors by size">
          <div className="text-xs text-neutral-300 space-y-1">
            {Object.entries(motors).map(([k, v]) => (
              <div key={k}>{k}: <b>{v}</b></div>
            ))}
          </div>
        </Section>
        <Section title="Cable trays (m by width)">
          <div className="text-xs text-neutral-300 space-y-1">
            {Object.entries(trays).map(([k, v]) => (
              <div key={k}>{k}: <b>{v}</b></div>
            ))}
          </div>
        </Section>
      </div>
      <Section title="Cables by (type × size)">
        <Table cols={[["cable_type","Type"], ["size_mm2","mm²"],
                        ["total_m","Total m"], ["procurement_m","Procure m"]]}
                 rows={cables} />
      </Section>
      <Section title="Lighting layout summary">
        <div className="text-xs text-neutral-300 space-y-1">
          <div>Total fixtures: <b>{light.total_fixtures ?? "-"}</b></div>
          <div>Lighting load: <b>{light.total_lighting_load_kW ?? "-"} kW</b></div>
          <div>Emergency backup: {light.emergency_battery_backup}</div>
        </div>
      </Section>
      <Section title="Completeness check">
        <div className="text-xs text-neutral-300 space-y-1">
          <div>Status: <b>{comp.status}</b> ({comp.percent_complete} %)</div>
          {(comp.missing || []).length > 0 && (
            <div className="text-amber-300">Missing: {(comp.missing || []).join(", ")}</div>
          )}
        </div>
      </Section>
      <button className="btn btn-secondary text-xs w-fit"
                onClick={async () => {
                  const r = await api.post(`/projects/${projectId}/stage-e/electrical-layout-svg`,
                                                  {plot_size_m: [120, 80], layout: {}, country: "IN"});
                  const blob = new Blob([r.data], {type: "image/svg+xml"});
                  window.open(URL.createObjectURL(blob), "_blank");
                }}
                data-testid="stage-e-elec-layout-svg-btn">
        Preview Electrical Layout SVG
      </button>
      <button className="btn btn-secondary text-xs w-fit"
                onClick={async () => {
                  const r = await api.post(`/projects/${projectId}/stage-e/motor-control-schematic`,
                                                  {motor: {tag: "M-101", rated_kW: 30, voltage_V: 415},
                                                   starting_method: "DOL"});
                  const blob = new Blob([r.data], {type: "image/svg+xml"});
                  window.open(URL.createObjectURL(blob), "_blank");
                }}
                data-testid="stage-e-motor-ctl-svg-btn">
        Preview Motor Control Schematic
      </button>
    </div>
  );
}

/* ═════════════════════ Wrapper ════════════════════════════════ */
export default function StageEPanel({ projectId, subStage }) {
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(true);

  const run = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.post(`/projects/${projectId}/stage-e/run`,
                                       {equipment: [], design_basis: {}, country: "IN"});
      setRes(r.data);
    } catch (e) {
      setRes({ error: e.message });
    } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { run(); }, [run]);

  return (
    <div className="space-y-4" data-testid={`stage-e-panel-${subStage}`}>
      <div className="flex items-center justify-between rounded-md border border-neutral-800 bg-neutral-950 p-3">
        <div>
          <div className="text-sm font-semibold text-neutral-100">Stage E · Electrical Engineering</div>
          <div className="text-xs text-neutral-400">
            Sub-stage {subStage} · powered by E.1-E.6 gap-closure engines (38 fixes)
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={run} className="btn btn-primary text-xs" data-testid="stage-e-rerun-btn">Re-run pipeline</button>
        </div>
      </div>

      {loading && (
        <div className="rounded-md border border-neutral-800 bg-neutral-950 p-4 text-xs text-neutral-500">
          Running Stage E pipeline · load list · motor start · SC · earthing · LPS · cables · MTO…
        </div>
      )}

      {!loading && res?.error && (
        <div className="rounded-md border border-red-900 bg-red-950/50 p-4 text-xs text-red-300">
          Pipeline error: {res.error}
        </div>
      )}

      {!loading && !res?.error && (
        <>
          {subStage === "E.1" && <E1LoadPower projectId={projectId} res={res} />}
          {subStage === "E.2" && <E2HazArea projectId={projectId} />}
          {subStage === "E.3" && <E3SldDiagrams projectId={projectId} res={res} />}
          {subStage === "E.4" && <E4Earthing projectId={projectId} res={res} />}
          {subStage === "E.5" && <E5Cable res={res} />}
          {subStage === "E.6" && <E6MTO projectId={projectId} res={res} />}
        </>
      )}
    </div>
  );
}
